import { ArrowTurnDownRightIcon, DocumentTextIcon, PlusIcon } from "@heroicons/react/24/outline";
import { unwrapConnection } from "@utils/utils";

import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@fronterahealth/frontera-ui-components/ui/dropdown-menu";

import {
  ReportSectionFieldsType,
  ReportSectionsType,
  useCreateReportSectionsMutation,
} from "@api/graphql/types-and-hooks";
import { useMoveFieldToSection } from "@components/ReportField/ReportFieldDropdownMenu/useMoveFieldToSection";
import { APPENDIX_PAGE_LOCATION } from "@components/ReportField/consts";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider";

interface Props {
  section: ReportSectionsType;
  field: ReportSectionFieldsType;
}

export const ReportFieldDropdownMenuMoveToAppendix: React.FC<Props> = ({ section, field }) => {
  const { report, reportQuery } = useReport();
  const moveToSectionMutate = useMoveFieldToSection();
  const createReportSections = useCreateReportSectionsMutation({
    onError: (error) => {
      console.error("Error creating appendix section: ", error);
      notifyError("Error creating appendix section");
    },
  });

  const reportSections = unwrapConnection(report.reportSections) ?? [];
  const appendixSections = reportSections
    .filter((target) => target.pageLocation === APPENDIX_PAGE_LOCATION)
    .sort((a, b) => a.rankOrder - b.rankOrder);
  const hasAppendix = appendixSections.length > 0;

  const handleCreateAppendixSectionAndMoveField = async () => {
    // Appendix sections sort after every other section.
    const maxRankOrder = Math.max(...reportSections.map((s) => s.rankOrder), 0);

    const data = await createReportSections.mutateAsync({
      reportId: report.id,
      sections: [{ title: "Appendix", rankOrder: maxRankOrder + 1, pageLocation: APPENDIX_PAGE_LOCATION }],
      runWorkflow: false,
    });
    if (!data.createReportSections?.status) {
      notifyError(data.createReportSections?.message ?? "Error creating appendix section");
    }
    const refetchResult = await reportQuery?.refetch();
    const newSections = unwrapConnection(refetchResult?.data?.getReports?.edges[0]?.node?.reportSections) ?? [];
    const newAppendixSection = newSections.find((s) => s.pageLocation === APPENDIX_PAGE_LOCATION);

    if (newAppendixSection) {
      moveToSectionMutate({ fieldId: field.id, targetSectionId: newAppendixSection.id });
    }
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <ArrowTurnDownRightIcon />
        Move to Appendix
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {!hasAppendix && (
            <DropdownMenuItem onClick={() => handleCreateAppendixSectionAndMoveField()}>
              <PlusIcon />
              Create new
            </DropdownMenuItem>
          )}
          {hasAppendix &&
            appendixSections.map((target) => (
              <DropdownMenuItem
                key={target.id}
                disabled={section.id === target.id}
                onClick={() => moveToSectionMutate({ fieldId: field.id, targetSectionId: target.id })}
              >
                <DocumentTextIcon />
                {target.title}
              </DropdownMenuItem>
            ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};
